import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { useForm, Controller } from "react-hook-form";
import { PhoneInput } from "../../components/PhoneInput";
import { ProductCard} from "../../components/ProductCard";
import { Pagination } from "../../components/Pagination";
import { TooltipModal } from "../../components/Tooltip";
import { Loader } from '../../components/Loader';
import { PAYMENT_METHODS } from "../../constants";
import { FontAwesomeIcon, } from "@fortawesome/react-fontawesome";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import { faXmark, faCircleQuestion, faTrash } from "@fortawesome/free-solid-svg-icons";
import { createOrder, createOrderByAi } from "../../services/orderService";
import { useAdmins } from "../../contexts/AdminsContext";
import { useOrders } from "../../contexts/OrdersContext";
import { StoreProductModal } from "../../components/StoreProductModal";
import { useProducts } from "../../contexts/ProductsContext";
import { useSuccessMessage } from "../../contexts/SuccessMessageContext";
import { useAdminData } from "../../contexts/AuthContext";
import { CouponSelector } from '../../components/CouponSelector';
import { IAppliedCoupon, validateCoupon } from '../../services/couponService';
import {
    FormField,
    Label,
    Input,
    Select,
    Textarea,
    InlineFormField,
    CheckboxContainer,
    Checkbox,
    ErrorMessage,
    PrimaryButton,
    DescriptionArea,
    PageHeader,
    Switch,
    StyledSwitch
} from "../../styles/global";

import {
    Form,
    Container,
    OrderDetail,
    NewOrderContainer,
    ProductList,
    ProductContainer,
    NewProductButton,
    PageHeaderActions,
    DiscountSwitch,
    DiscountSwitchLabel,
    PriceSummary,
} from "./style";

import {
    DiscountSectionWrapper,
    SubsectionTitle,
    ManualDiscountContainer,
    Divider,
    DisabledNote,
    DiscountAppliedText,
    CouponContainer,
    CouponSectionHeader,
    ApplyCouponButton,
    AppliedCouponRow,
    CouponCode,
    CouponDiscountValue,
    CouponActions,
    ChangeCouponButton,
    RemoveCouponButton
} from "../OnStoreOrder/style";

import placeholder_products from '../../assets/images/placeholder_products.png';
import { RichTextEditor } from "../../components/RichTextEditor";

interface INewOrder {
    id: string;
    description: string;
    additional_information: string;
    has_card: boolean;
    payment_received: boolean;
    delivery_date: string;
    payment_method: string;
    products_value: any;
    discount: number;
    delivery_fee: number;
    total: number;
    created_by: string;
    online_code: string;
    receiver_phone: string;
    receiver_country_code: string;
    order_ai_information: string;
}

interface IProduct {
    id: string;
    name: string;
    price: number;
    quantity: number;
}

export function OnlineOrder() {
    const { products: availableProducts, loadAvailableProducts, totalProducts, refreshProducts } = useProducts();
    const { showSuccess } = useSuccessMessage();
    const { adminData } = useAdminData();

    const navigate = useNavigate();
    const { admins } = useAdmins();
    const { addOrder } = useOrders();

    const [showLoader, setShowLoader] = useState(false);
    const [showOrderDetail, setShowOrderDetail] = useState(false);
    const [orderLink, setOrderLink] = useState("");
    const [orderCode, setOrderCode] = useState("");
    const mockedDeliveryDate = moment().add(2, "days").format("YYYY-MM-DD");
    const [copied, setCopied] = useState(false);
    const [query, setQuery] = useState('');
    const [products, setProducts] = useState<IProduct[]>([]);
    const [productModal, setProductModal] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(8);
    const [useAIToGenerate, setUseAIToGenerate] = useState(false);
    const [showToolTipModal, setShowToolTipModal] = useState(false);
    const [isPercentageDiscount, setIsPercentageDiscount] = useState(false);
    const [appliedCoupon, setAppliedCoupon] = useState<IAppliedCoupon | null>(null);
    const [showCouponModal, setShowCouponModal] = useState(false);
    const tooltipMessage = `
Me envie os dados do pedido 😊

Pode ser tudo junto, por exemplo:

Entrega: 10/05
Remetente: João (22 99999-9999)
Destinatário: Maria (22 98888-8888)
Endereço: Rua X, 123 - Bairro Y
Referência: perto da praça
Cartão: Desejamos um feliz aniversário!`

    const {
        register,
        handleSubmit,
        formState: { errors },
        watch,
        setValue,
        control
    } = useForm<INewOrder>({
        defaultValues: {
            receiver_country_code: "BR"
        }
    });

    // Calcula o desconto absoluto baseado no tipo (% ou valor)
    const calculateAbsoluteDiscount = (discountValue: number, productsValue: number) => {
        if (!discountValue) return 0;
        if (isPercentageDiscount) {
            return (productsValue * discountValue) / 100;
        }
        return discountValue;
    };

    // Coupon handlers
    const handleOpenCouponModal = () => {
        setShowCouponModal(true);
    };

    const handleCloseCouponModal = () => {
        setShowCouponModal(false);
    };

    const handleApplyCoupon = (coupon: IAppliedCoupon) => {
        setAppliedCoupon(coupon);
        // Limpa o desconto manual quando um cupom é aplicado
        setValue('discount', 0);
        setIsPercentageDiscount(false);
        setShowCouponModal(false);
    };

    const handleRemoveCoupon = () => {
        setAppliedCoupon(null);
    };

    // Watch para recalcular o total quando mudar desconto ou produtos
    const productsValue = watch("products_value") || 0;
    const discountInput = watch("discount") || 0;
    const deliveryFee = watch("delivery_fee") || 0;

    // O cupom tem prioridade sobre o desconto manual
    const finalDiscount = appliedCoupon
        ? appliedCoupon.discount_amount
        : calculateAbsoluteDiscount(Number(discountInput), Number(productsValue));

    const absoluteDiscount = finalDiscount;
    const totalValue = Number(productsValue) - finalDiscount + Number(deliveryFee);

    // Re-valida o cupom aplicado sempre que o valor dos produtos mudar,
    // pois a remoção/redução de itens pode fazer o pedido deixar de atender
    // as regras do cupom (valor mínimo, etc).
    useEffect(() => {
        if (!appliedCoupon) return;

        let isCurrent = true;

        const revalidate = async () => {
            try {
                const response = await validateCoupon({
                    code: appliedCoupon.code,
                    store_id: adminData.store_id!,
                    orderTotal: Number(productsValue)
                });

                if (!isCurrent) return;

                if (response.data.valid) {
                    if (response.data.discount_amount !== appliedCoupon.discount_amount) {
                        setAppliedCoupon({
                            ...appliedCoupon,
                            discount_amount: response.data.discount_amount!
                        });
                    }
                } else {
                    setAppliedCoupon(null);
                }
            } catch {
                if (isCurrent) setAppliedCoupon(null);
            }
        };

        revalidate();

        return () => {
            isCurrent = false;
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productsValue]);

    const handleCopy = () => {
        const message = `Você poderia preencher esse link com o endereço completo prfv? E nele também tem um espacinho para você enviar um cartão. ✉️❤️\nNº do pedido: #${orderCode}\n${orderLink}`;

        navigator.clipboard.writeText(message).then(() => {
            setCopied(true);
            setTimeout(() => {
                setCopied(false);
            }, 2000);
        });
    };

    function generateOnlineCode() {
        const numerosAleatorios = Array.from({ length: 4 }, () => Math.floor(Math.random() * 10)).join('');
        const timestamp = Date.now().toString().slice(0, 3);
        return `${numerosAleatorios}${timestamp}`;
    }

    const submitOrder = async (data: INewOrder) => {
        const baseUrl = process.env.REACT_APP_URL;

        setShowLoader(true);

        // Calcula o desconto absoluto para enviar ao backend (cupom tem prioridade)
        const absoluteDiscountValue = appliedCoupon
            ? appliedCoupon.discount_amount
            : calculateAbsoluteDiscount(Number(data.discount) || 0, Number(data.products_value));

        console.log(appliedCoupon, "appliedCoupon");
        const orderData = {
            client_id: null,
            receiver_phone: data.receiver_phone,
            receiver_country_code: data.receiver_country_code,
            first_name: "",
            last_name: "",
            receiver_name: "",
            addressId: "",
            pickup_on_store: false,
            street: "",
            street_number: "",
            complement: "",
            reference_point: "",
            neighborhood: "",
            city: "",
            state: "",
            postal_code: "",
            country: "",
            description: data.description,
            additional_information: data.additional_information,
            delivery_date: mockedDeliveryDate,
            payment_method: data.payment_method,
            payment_received: data.payment_received,
            products_value: Number(data.products_value),
            discount: absoluteDiscountValue,
            delivery_fee: Number(data.delivery_fee),
            total: Number(data.products_value) - absoluteDiscountValue + Number(data.delivery_fee),
            status: "WAITING_FOR_CLIENT",
            has_card: false,
            created_by: data.created_by,
            online_order: true,
            online_code: generateOnlineCode(),
            products: products,
            order_ai_information: data.order_ai_information,
            // Coupon data
            coupon_code: appliedCoupon?.code || undefined,
            coupon_id: appliedCoupon?.coupon_id || undefined,
        }

        let response;

        if (!useAIToGenerate) {
            const { data: responseData } = await createOrder(orderData);
            response = responseData;
        }

        if (useAIToGenerate) {
            const { data: responseData } = await createOrderByAi(orderData);
            response = responseData;
            addOrder(response);
            setShowLoader(false);
            navigate("/backoffice/ordensDeServico");
            return;
        }

        if (response.id && !useAIToGenerate) {
            setShowOrderDetail(true);
            setOrderLink(response.short_code
                ? `${baseUrl}pedido/${response.short_code}`
                : `${baseUrl}completarPedido/${response.id}`);
            setOrderCode(response.code);
        }

        addOrder(response);
        showSuccess("Pedido online criado com sucesso!");

        setShowLoader(false);
    }

    const handleSearchProducts = (text: string) => {
        setQuery(text);
        setPage(1);
    };

    const handleAddProduct = (product: IProduct, quantity: number, price: number) => {
        setProducts((prev: IProduct[]) => {
            const existingIndex = prev.findIndex((p) => p.id === product.id);
            let updated;

            if (existingIndex !== -1) {
                const existing = prev[existingIndex];
                const newQuantity = existing.quantity + quantity;

                // Recalcula o preço médio ponderado
                const newPrice = (
                    (existing.quantity * existing.price + quantity * price) /
                    newQuantity
                );

                updated = [...prev];
                updated[existingIndex] = {
                    ...existing,
                    quantity: newQuantity,
                    price: Number(newPrice.toFixed(2)),
                };
            } else {
                updated = [
                    ...prev,
                    {
                        ...product,
                        quantity,
                        price: Number(price),
                    },
                ];
            }

            const total = updated.reduce((sum, p) => {
                return sum + Number(p.quantity) * Number(p.price);
            }, 0);

            const formattedTotal = total > 0 ? total.toFixed(2) : 0;

            setValue("products_value", formattedTotal);
            return updated;
        });
    };

    const removeProduct = (product: any) => {
        setProducts((prev: any) => {
            const updated = prev.filter((p: any) => p.id !== product.id);
        
            const total = updated.reduce((sum: any, p: any) => {
                return sum + Number(p.quantity) * Number(p.price);
            }, 0);

            const formattedTotal = total > 0 ? total.toFixed(2) : 0;
        
            setValue("products_value", formattedTotal);
        
            return updated;
        });
    };

    function handleOpenProductModal(product: any){
        setProductModal(true)
    }
    function handleCloseProductModal(){
        setProductModal(false)
    }

    const description = products
        .map((p) => `${p.quantity}x ${p.name} - R$ ${p.price}`)
        .join('\n');

    useEffect(() => {
        setValue('description', description);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [description]);

    console.log('products', products.length)

    useEffect(() => {
        if (!adminData.store_id) return;
        setShowLoader(true);
        setTimeout(() => {
            loadAvailableProducts(adminData.store_id!, page, pageSize, query).then(() => {
                setShowLoader(false);
            }).catch(() => {
                setShowLoader(false);
            });
        }, 300);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, pageSize, query, adminData.store_id]);

    useEffect(() => {
        function handleResize() {
            const width = window.innerWidth;
            if (width < 800) {
                setPageSize(1);
            } else if (width < 1300) {
                setPageSize(4);
            } 
            else if (width < 1600) {
                setPageSize(6);
            }
            else if (width < 1830) {
                setPageSize(8);
            } else {
                setPageSize(10);
            }
        }

        handleResize();
    }, []);

    // Bloco de Desconto Manual + Cupom, reutilizado nos dois modos (link/IA)
    const renderDiscountAndCoupon = () => (
        <DiscountSectionWrapper>
            {/* Desconto Manual */}
            <SubsectionTitle>Desconto Manual</SubsectionTitle>
            <ManualDiscountContainer $active={!appliedCoupon}>
                <Divider />

                <DiscountSwitch>
                    <span style={{ color: !isPercentageDiscount ? "#EC4899" : "#5B5B5B" }}>R$</span>
                    <input
                        id="discount-switch-online"
                        type="checkbox"
                        checked={isPercentageDiscount}
                        onChange={() => setIsPercentageDiscount(!isPercentageDiscount)}
                        disabled={!!appliedCoupon}
                        style={{ display: 'none' }}
                    />
                    <DiscountSwitchLabel htmlFor="discount-switch-online" $checked={isPercentageDiscount} />
                    <span style={{ color: isPercentageDiscount ? "#EC4899" : "#5B5B5B" }}>%</span>
                </DiscountSwitch>

                <Input
                    type="number"
                    step="0.01"
                    placeholder={isPercentageDiscount ? "0.00%" : "0.00"}
                    defaultValue={0}
                    {...register("discount")}
                    disabled={!!appliedCoupon}
                />
            </ManualDiscountContainer>

            {appliedCoupon && (
                <DisabledNote>
                    Remova o cupom para aplicar desconto manual
                </DisabledNote>
            )}

            {!appliedCoupon && absoluteDiscount > 0 && (
                <DiscountAppliedText>
                    💰 Desconto aplicado: R$ {absoluteDiscount.toFixed(2)}
                </DiscountAppliedText>
            )}

            {/* Cupom */}
            <CouponContainer $applied={!!appliedCoupon}>
                <CouponSectionHeader>
                    <SubsectionTitle>Cupom de Desconto</SubsectionTitle>

                    {appliedCoupon && (
                        <CouponActions>
                            <ChangeCouponButton
                                type="button"
                                onClick={handleOpenCouponModal}
                            >
                                ✏️
                            </ChangeCouponButton>
                            <RemoveCouponButton
                                type="button"
                                className="del-button"
                                onClick={handleRemoveCoupon}
                            >
                                <FontAwesomeIcon icon={faTrash as any} />
                            </RemoveCouponButton>
                        </CouponActions>
                    )}
                </CouponSectionHeader>

                {!appliedCoupon ? (
                    <>
                        <ApplyCouponButton
                            type="button"
                            onClick={handleOpenCouponModal}
                            disabled={discountInput > 0 || products.length === 0}
                        >
                            🎁 Aplicar Cupom
                        </ApplyCouponButton>

                        {discountInput > 0 && (
                            <DisabledNote>
                                Remova o desconto manual para aplicar cupom
                            </DisabledNote>
                        )}
                    </>
                ) : (
                    <AppliedCouponRow>
                        <CouponCode>🎁 {appliedCoupon.code}</CouponCode>
                        <CouponDiscountValue>R$ -{appliedCoupon.discount_amount.toFixed(2)}</CouponDiscountValue>
                    </AppliedCouponRow>
                )}
            </CouponContainer>
        </DiscountSectionWrapper>
    );

    return (
        <Container>
            <TooltipModal
                isOpen={showToolTipModal}
                onRequestClose={() => setShowToolTipModal(false)}
                textContent={tooltipMessage}
                title="Utilize o modelo abaixo, se atente a questão da Cidade e do Estado caso não seja Itaperuna."
                showWhatsapp={false}
                showCopyButton={true}
            />
            <StoreProductModal 
                isOpen={productModal}
                onRequestClose={handleCloseProductModal}
                loadData={(storeId) => refreshProducts(storeId)}
                action={"create"}
                currentProduct={{
                    id: "",
                    name: "",
                    image: "",
                    price: 0,
                    unity: "",
                    stock: 0,
                    enabled: false
                }}
            />

            <CouponSelector
                isOpen={showCouponModal}
                onRequestClose={handleCloseCouponModal}
                onSelectCoupon={handleApplyCoupon}
                orderTotal={Number(productsValue) + Number(deliveryFee)}
                storeId={adminData.store_id!}
                customerId={undefined}
            />

            {showOrderDetail &&
                <OrderDetail>
                    <div className="order-detail-container">
                        <h2>Pedido criado com sucesso</h2>
                        <p>
                            Passe para o cliente os dados abaixo, para que ele possa completar as informações
                            no formulário:
                        </p>
                        <div>
                            <p>
                                <strong>Link do pedido: </strong>
                                {orderLink}
                            </p>
                            <button
                                type="button"
                                onClick={handleCopy}
                                style={{
                                    padding: "4px 8px",
                                    backgroundColor: "#e7b7c2",
                                    border: "none",
                                    borderRadius: "4px",
                                    cursor: "pointer",
                                    color: 'white',
                                    fontSize: '15px'
                                }}
                            >
                                Copiar link
                            </button>
                            {copied && (
                                <span style={{ color: "gray", fontWeight: "300", marginLeft: '10px' }}>
                                    Link copiado!
                                </span>
                            )}
                        </div>
                    </div>
                </OrderDetail>
            }
            <Loader show={showLoader} />
            {!showOrderDetail &&
                <NewOrderContainer>
                    <ProductContainer>
                        <PageHeader>
                            <div className="title">
                                <FontAwesomeIcon icon={faWhatsapp as any} />
                                <span>Pedido Online</span>
                            </div>
                            <PageHeaderActions>
                                <Switch>
                                    <span style={{ color: useAIToGenerate ? "#333" : "#EC4899" }}>
                                        <i className="material-icons">link</i>
                                        Pedido via Link
                                    </span>
                                    <Input id="switch" type="checkbox" placeholder='Ativo'
                                        onChange={(e: any) => setUseAIToGenerate(e.target.checked)}/>
                                    <StyledSwitch htmlFor="switch" $checked={useAIToGenerate as boolean} />
                                    <span style={{ color: useAIToGenerate ? "#EC4899" : "#333" }}>
                                        <i className="material-icons">smart_toy</i>
                                        Pedido via IA
                                    </span>
                                </Switch>
                                <NewProductButton type="button" onClick={handleOpenProductModal}>
                                    Novo Produto
                                </NewProductButton>
                            </PageHeaderActions>
                        </PageHeader>
                        <div className="product-data">
                            <div style={{ position: 'relative', width: '100%' }}>
                                <Input
                                    placeholder="Buscar Produtos"
                                    onKeyDown={(e: any) => {
                                        if (e.key === 'Enter') {
                                            handleSearchProducts(e.target.value);
                                        }
                                    }}
                                />
                            </div>
                        </div>
                        <ProductList>
                            {availableProducts?.map((product: any) => (
                                <ProductCard
                                    key={product.id}
                                    product={product}
                                    image={product.image ? product.image : placeholder_products}
                                    onAdd={handleAddProduct}
                                />
                            ))}

                        </ProductList>
                        <Pagination
                            currentPage={page}
                            total={totalProducts}
                            pageSize={pageSize as number}
                            onPageChange={setPage}
                        />
                    </ProductContainer>
                    <Form onSubmit={handleSubmit(submitOrder)}>
                        <FormField>
                            <Label>Produtos</Label>
                            <DescriptionArea>
                                {products.map((p, index) => (
                                    <p key={index}>
                                        {p.quantity}x {p.name} - R$ {p.price}
                                        <button type="button" onClick={() => removeProduct(p)}>
                                            <FontAwesomeIcon icon={faXmark}/>
                                        </button>
                                    </p>
                                ))}
                            </DescriptionArea>
                            <Input {...register("description", { required: "Descrição obrigatória" })}
                                style={{ display: 'none'}} value={watch("description")}/>

                            {errors.description && (
                                <ErrorMessage>{errors.description.message}</ErrorMessage>
                            )}
                        </FormField>
                        {!useAIToGenerate &&
                            <>
                                <FormField>
                                    <Label>Observações</Label>
                                    <Controller
                                        name="additional_information"
                                        control={control}
                                        render={({ field }) => (
                                            <RichTextEditor
                                                value={field.value}
                                                onChange={field.onChange}
                                            />
                                        )}
                                    />
                                </FormField>
                                <FormField>
                                    <Label>
                                        Telefone do cliente
                                        <span>*</span>
                                    </Label>
                                    <PhoneInput
                                        control={control}
                                        setValue={setValue}
                                        phoneFieldName="receiver_phone"
                                        countryFieldName="receiver_country_code"
                                        required
                                    />
                                    {errors.receiver_phone && <ErrorMessage>{errors.receiver_phone.message}</ErrorMessage>}
                                </FormField>
                                <InlineFormField style={{ alignItems: "center" }}>
                                    <FormField style={{ marginRight: "20px" }}>
                                        <Label>
                                            Método de pagamento
                                            <span>*</span>
                                        </Label>
                                        <Select {...register("payment_method", { required: "Método de pagamento inválido" })}>
                                            <option value="">Selecione um método</option>
                                            {Object.entries(PAYMENT_METHODS).map(([key, value]) => (
                                                <option key={key} value={key}>{value}</option>
                                            ))}
                                        </Select>
                                        {errors.payment_method && <ErrorMessage>{errors.payment_method.message}</ErrorMessage>}
                                    </FormField>
                                    <FormField>
                                        <CheckboxContainer>
                                            <Checkbox type="checkbox" {...register("payment_received")} />
                                            <Label>Pagamento Recebido</Label>
                                        </CheckboxContainer>
                                    </FormField>
                                </InlineFormField>
                                <FormField>
                                    <Label>Vendedor Responsável</Label>
                                    <Select {...register("created_by", { required: "Vendedor Responsável inválido" })}>
                                        <option value="">Selecione um Vendedor</option>
                                        {admins.map((admin: any) => (
                                            <option key={admin.id} value={admin.id}>{admin.name}</option>
                                        ))}
                                    </Select>
                                    {errors.created_by && <ErrorMessage>{errors.created_by.message}</ErrorMessage>}
                                </FormField>
                                <InlineFormField>
                                    <FormField>
                                        <Label>Total dos Produtos</Label>
                                        <Input type="number" step="0.01" placeholder="Total" value={watch("products_value")}
                                            {...register("products_value", {
                                                required: "Valor total é obrigatório",
                                            })}
                                        />
                                        {errors.products_value && <ErrorMessage>{errors.products_value.message}</ErrorMessage>}
                                    </FormField>
                                    <FormField>
                                        <Label>Taxa de entrega</Label>
                                        <Input type="number" step="0.01" placeholder="0.00" {...register("delivery_fee", {
                                        })} />
                                    </FormField>
                                </InlineFormField>

                                {renderDiscountAndCoupon()}

                                <PriceSummary>
                                    <div className="summary-line">
                                        <span>Subtotal (Produtos):</span>
                                        <span>R$ {Number(productsValue).toFixed(2)}</span>
                                    </div>
                                    <div className="summary-line">
                                        <span>Desconto {appliedCoupon ? `(🎁 ${appliedCoupon.code})` : (isPercentageDiscount ? `(${Number(discountInput).toFixed(2)}%)` : '')}:</span>
                                        <span>- R$ {absoluteDiscount.toFixed(2)}</span>
                                    </div>
                                    <div className="summary-line">
                                        <span>Taxa de Entrega:</span>
                                        <span>R$ {Number(deliveryFee).toFixed(2)}</span>
                                    </div>
                                    <div className="summary-total">
                                        <span>Total:</span>
                                        <span>R$ {totalValue.toFixed(2)}</span>
                                    </div>
                                </PriceSummary>
                            </>
                        }

                        {useAIToGenerate &&
                            <>
                                <FormField>
                                    <Label>Observações</Label>
                                    <Controller
                                        name="additional_information"
                                        control={control}
                                        render={({ field }) => (
                                            <RichTextEditor
                                                value={field.value}
                                                onChange={field.onChange}
                                            />
                                        )}
                                    />
                                </FormField>
                                <InlineFormField style={{ alignItems: "center" }}>
                                    <FormField style={{ marginRight: "20px" }}>
                                        <Label>
                                            Método de pagamento
                                            <span>*</span>
                                        </Label>
                                        <Select {...register("payment_method", { required: "Método de pagamento inválido" })}>
                                            <option value="">Selecione um método</option>
                                            {Object.entries(PAYMENT_METHODS).map(([key, value]) => (
                                                <option key={key} value={key}>{value}</option>
                                            ))}
                                        </Select>
                                        {errors.payment_method && <ErrorMessage>{errors.payment_method.message}</ErrorMessage>}
                                    </FormField>
                                    <FormField>
                                        <CheckboxContainer>
                                            <Checkbox type="checkbox" {...register("payment_received")} />
                                            <Label>Pagamento Recebido</Label>
                                        </CheckboxContainer>
                                    </FormField>
                                </InlineFormField>
                                <FormField>
                                    <Label>Vendedor Responsável</Label>
                                    <Select {...register("created_by", { required: "Vendedor Responsável inválido" })}>
                                        <option value="">Selecione um Vendedor</option>
                                        {admins.map((admin: any) => (
                                            <option key={admin.id} value={admin.id}>{admin.name}</option>
                                        ))}
                                    </Select>
                                    {errors.created_by && <ErrorMessage>{errors.created_by.message}</ErrorMessage>}
                                </FormField>
                                <InlineFormField>
                                    <FormField>
                                        <Label>Total dos Produtos</Label>
                                        <Input type="number" step="0.01" placeholder="Total" value={watch("products_value")}
                                            {...register("products_value", {
                                                required: "Valor total é obrigatório",
                                            })}
                                        />
                                        {errors.products_value && <ErrorMessage>{errors.products_value.message}</ErrorMessage>}
                                    </FormField>
                                    <FormField>
                                        <Label>Taxa de entrega</Label>
                                        <Input type="number" step="0.01" placeholder="0.00" {...register("delivery_fee", {
                                        })} />
                                    </FormField>
                                </InlineFormField>

                                {renderDiscountAndCoupon()}

                                <PriceSummary>
                                    <div className="summary-line">
                                        <span>Subtotal (Produtos):</span>
                                        <span>R$ {Number(productsValue).toFixed(2)}</span>
                                    </div>
                                    <div className="summary-line">
                                        <span>Desconto {appliedCoupon ? `(🎁 ${appliedCoupon.code})` : (isPercentageDiscount ? `(${Number(discountInput).toFixed(2)}%)` : '')}:</span>
                                        <span>- R$ {absoluteDiscount.toFixed(2)}</span>
                                    </div>
                                    <div className="summary-line">
                                        <span>Taxa de Entrega:</span>
                                        <span>R$ {Number(deliveryFee).toFixed(2)}</span>
                                    </div>
                                    <div className="summary-total">
                                        <span>Total:</span>
                                        <span>R$ {totalValue.toFixed(2)}</span>
                                    </div>
                                </PriceSummary>

                                <FormField>
                                    <Label>
                                        <div>
                                            Infomações do Cliente
                                            <span>*</span>
                                        </div>

                                        <button
                                            style={{ left: "0px", right: "50px" }}
                                            type="button"
                                            className="label-question"
                                            onClick={() => setShowToolTipModal(!showToolTipModal)}>
                                            <FontAwesomeIcon icon={faCircleQuestion} />
                                        </button>
                                    </Label>
                                    <Textarea
                                        style={{ minHeight: "150px" }}
                                        placeholder="Infomações do Cliente"
                                        {...register("order_ai_information")}
                                    />
                                </FormField>
                            </>
                        }
                        
                        <PrimaryButton type="submit">Finalizar Pedido</PrimaryButton>
                    </Form>
                </NewOrderContainer>
            }
        </Container>
    );
}